import React, { useState, useEffect } from 'react';
import './Drivers.css';
import { toast } from 'react-toastify';
import { Search, Pencil, X } from 'lucide-react';
import { driversAPI } from '../../config/drivers-api';
import { helpers } from '../../utils/helpers';
import { isIndiaClient } from '../../utils/client';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';

const cleanQatarPhone = (phone) => {
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('974')) {
    cleaned = cleaned.slice(3);
  } else if (cleaned.startsWith('00974')) {
    cleaned = cleaned.slice(5);
  }
  return cleaned.slice(0, 8);
};

const cleanIndiaPhone = (phone) => {
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('91')) {
    cleaned = cleaned.slice(2);
  } else if (cleaned.startsWith('0091')) {
    cleaned = cleaned.slice(4);
  } else if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.slice(0, 10);
};

const emptyForm = {
  name: '',
  phoneNumber: '',
  countryCode: '',
  color: '#3B82F6',
  status: true,
  profilePicture: '',
  profilePictureFile: null,
};

const Drivers = ({ adminData }) => {
  const isIndia = isIndiaClient(adminData);

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_DRIVERS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_DRIVERS);
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_DRIVERS);

  const fetchDrivers = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await driversAPI.getDriversList({
        page,
        limit: 20,
        search: searchQuery || undefined,
      });

      if (response.success) {
        setDrivers(response.data || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to fetch drivers');
      }
    } catch (err) {
      setError('Error fetching drivers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchDrivers(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, canView]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phoneNumber') {
      const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;
      setFormData((prev) => ({
        ...prev,
        phoneNumber: cleaner(value),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddNew = () => {
    setEditMode(false);
    setSelectedDriverId(null);
    setFormData({
      ...emptyForm,
      countryCode: isIndia ? '+91' : '+974',
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (driver) => {
    setEditMode(true);
    setSelectedDriverId(driver._id);
    setFormData({
      name: driver.name || '',
      phoneNumber: driver.phoneNumber || '',
      countryCode: driver.countryCode || (isIndia ? '+91' : '+974'),
      color: driver.color || '#3B82F6',
      status: driver.status !== false,
      profilePicture: driver.profilePicture || '',
      profilePictureFile: null,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (formData.profilePicture && formData.profilePicture.startsWith('blob:')) {
      URL.revokeObjectURL(formData.profilePicture);
    }
    setIsDialogOpen(false);
    setEditMode(false);
    setSelectedDriverId(null);
    setFormData(emptyForm);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => {
      if (prev.profilePicture && prev.profilePicture.startsWith('blob:')) {
        URL.revokeObjectURL(prev.profilePicture);
      }
      return {
        ...prev,
        profilePicture: previewUrl,
        profilePictureFile: file,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!formData.name.trim()) {
        toast.error('Driver name is required');
        return;
      }
      const localPhone = formData.phoneNumber.trim();
      if (!localPhone) {
        toast.error('Phone number is required');
        return;
      }
      if (isIndia) {
        if (localPhone.length !== 10) {
          toast.error('Phone number must be exactly 10 digits');
          return;
        }
      } else {
        if (!/^\d{8}$/.test(localPhone)) {
          toast.error('Phone number must be exactly 8 digits');
          return;
        }
      }
      if (!formData.countryCode.trim()) {
        toast.error('Country code is required');
        return;
      }

      const payload = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        countryCode: formData.countryCode.trim(),
        color: formData.color,
        status: formData.status,
        profilePicture: formData.profilePicture,
        profilePictureFile: formData.profilePictureFile,
      };

      let result;
      if (editMode) {
        result = await driversAPI.updateDriver(selectedDriverId, payload);
      } else {
        result = await driversAPI.createDriver(payload);
      }

      if (result.success) {
        toast.success(editMode ? 'Driver updated successfully' : 'Driver created successfully');
        handleCloseDialog();
        fetchDrivers(currentPage);
      } else {
        toast.error(result.error || (editMode ? 'Failed to update driver' : 'Failed to create driver'));
      }
    } catch (err) {
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the driver`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canView) {
    return (
      <div className="driverlist-page">
        <div className="driverlist-empty-state">
          <p>You do not have permission to view drivers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="driverlist-page">
      <div className="driverlist-page-header">
        <div className="driverlist-page-title">
          <h1>Drivers</h1>
          <p>View and manage delivery drivers</p>
        </div>
        {canCreate && (
          <button className="driverlist-create-btn" onClick={handleAddNew}>
            Add Driver
          </button>
        )}
      </div>

      <div className="driverlist-search-bar-container">
        <form className="driverlist-search-form" onSubmit={handleSearch}>
          <div className="driverlist-search-input-wrapper">
            <Search size={18} className="driverlist-search-icon" />
            <input
              type="text"
              className="driverlist-search-input"
              placeholder="Search by name or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="driverlist-search-btn">Search</button>
          {searchQuery && (
            <button type="button" className="driverlist-reset-btn" onClick={handleResetSearch}>
              <X size={18} />
            </button>
          )}
        </form>
      </div>

      <div className="driverlist-table-container">
        {loading ? (
          <div className="driverlist-loading-state">
            <div className="driverlist-loading-spinner" />
            <p>Loading drivers...</p>
          </div>
        ) : error ? (
          <div className="driverlist-error-state">
            <p>{error}</p>
            <button onClick={() => fetchDrivers(currentPage)} className="driverlist-retry-button">
              Retry
            </button>
          </div>
        ) : drivers.length === 0 ? (
          <div className="driverlist-empty-state">
            <p>No drivers found</p>
          </div>
        ) : (
          <>
            <div className="driverlist-table-wrapper">
              <table className="driverlist-table">
                <thead>
                  <tr>
                    <th className="driverlist-pic-cell">Pic</th>
                    <th>Color</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Created</th>
                    {canEdit && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <tr key={driver._id}>
                      <td className="driverlist-pic-cell">
                        <div className="driverlist-table-pic-wrapper">
                          <img 
                            src={driver.profilePicture || '/images/default_admin_pic.jpeg'} 
                            alt={driver.name} 
                            className="driverlist-table-pic"
                            onError={(e) => { e.target.src = '/images/default_admin_pic.jpeg'; }}
                          />
                        </div>
                      </td>
                      <td>
                        <span
                          className="driverlist-color-dot"
                          style={{ backgroundColor: driver.color || '#3B82F6' }}
                        />
                      </td>
                      <td className="driverlist-name-cell">{driver.name}</td>
                      <td>{driver.countryCode} {driver.phoneNumber}</td>
                      <td>
                        <span className={`driverlist-status-badge ${driver.status !== false ? 'active' : 'inactive'}`}>
                          {driver.status !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{driver.createdAt ? new Date(driver.createdAt).toLocaleDateString() : '-'}</td>
                      {canEdit && (
                        <td>
                          <button
                            onClick={() => handleEdit(driver)}
                            className="driverlist-edit-btn"
                            title="Edit Driver"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="driverlist-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="driverlist-pagination-button"
                >
                  Previous
                </button>
                <span className="driverlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="driverlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isDialogOpen && (
        <div className="driverlist-dialog-overlay">
          <div className="driverlist-dialog-box">
            <div className="driverlist-dialog-header">
              <h2>{editMode ? 'Edit Driver' : 'Add Driver'}</h2>
              <button className="driverlist-close-btn" onClick={handleCloseDialog}>&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="driverlist-form">
              {editMode && (
                <div className="driverlist-profile-section">
                  <div className="driverlist-profile-upload-wrapper">
                    <div className="driverlist-profile-upload-container">
                      <div className="driverlist-profile-preview-container">
                        <img
                          src={formData.profilePicture || '/images/default_admin_pic.jpeg'}
                          alt={formData.name || 'Driver'}
                          className="driverlist-profile-upload-preview"
                          onError={(e) => { e.target.src = '/images/default_admin_pic.jpeg'; }}
                        />
                      </div>
                      <label htmlFor="driverProfilePicture" className="driverlist-profile-edit-badge" title="Upload Photo">
                        <Pencil size={14} />
                        <input
                          type="file"
                          id="driverProfilePicture"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="driverlist-form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Driver name"
                />
              </div>

              <div className="driverlist-form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <div className="driverlist-phone-input-wrapper">
                  <span className="driverlist-phone-prefix">{isIndia ? '+91' : '+974'}</span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                    maxLength={isIndia ? 10 : 8}
                  />
                </div>
              </div>

              <div className="driverlist-form-group">
                <label htmlFor="color">Color</label>
                <div className="driverlist-color-input-row">
                  <input
                    type="color"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div className="driverlist-form-group">
                <label className="driverlist-checkbox-label">
                  <input
                    type="checkbox"
                    name="status"
                    checked={formData.status}
                    onChange={handleInputChange}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="driverlist-form-actions">
                <button type="button" className="driverlist-cancel-btn" onClick={handleCloseDialog} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="driverlist-submit-btn" disabled={submitting}>
                  {submitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Driver' : 'Create Driver')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
